import { _ } from "../../../core/polyglot/polyglot.js";
import { eventManager } from "@foretagsplatsen/yaem";
import FilePermissions from "./filePermissions/FilePermissions.js";
import Model from "../../Model.js";

/**
 * `entry` is an abstract class representing a file as seen in UNIX,
 * aka a regular file or a folder (we omit here symlinks).
 * Since regular files and folders should be polymorphic,
 * this abstract model provide the public (and polymorphic) API.
 *
 * See `folder` and `file` for concrete implementations.
 *
 * An entry is a read-only object built using the `EntriesFactory`.
 *
 * @abstract
 * @param {string} spec.name - Name if the entry within the filesystem
 * @param {FilePermissions} spec.filePermissions - File permissions indicating what can be done with this entry
 * @param {{}} [spec.id=null] - An object which can undersands `toString()`
 * @param {string} [spec.creationTime=''] - A string representing the time at which the entry was created
 * @param {string} [spec.lastModificationTime=''] - A string representing the time at which the entry was last modified
 */

/**
 * TODO: write documentation
 */
export default class Entry extends Model {
	constructor({
		name = "",
		filePermissions,
		id = null,
		creationTime = "",
		lastModificationTime = "",
	} = {}) {
		super(...arguments);

		this._name = name;
		this._filePermissions = filePermissions;
		this._id = id;
		this._creationTime = creationTime;
		this._lastModificationTime = lastModificationTime;
		this._parent = null;

		// The same eventHandler is reused for all models
		// representing the same entry.
		this._eventHandler = eventManager.at(this._identifier());
		this.onWidgetBuilt = this._eventHandler.createEvent("widgetBuilt");
	}

	getFilePermissions() {
		return this._filePermissions;
	}

	hasChildren() {
		return false;
	}

	children() {
		return [];
	}

	name() {
		return this._name;
	}

	setName(newName) {
		this._name = newName;
	}

	size() {
		return 0;
	}

	id() {
		return this._id;
	}

	creationTime() {
		return this._creationTime;
	}

	parent() {
		return this._parent;
	}

	lastModificationTime() {
		return this._lastModificationTime;
	}

	getFullPath() {
		const parents = [];
		let current = this;
		while (current !== null) {
			parents.unshift(current);
			current = current.parent();
		}

		return parents;
	}

	findChildWithId(id) {
		if (this.id() === id) {
			return this;
		}

		if (id === "") {
			return this.root();
		}

		let found = null;

		this.children().forEach((each) => {
			if (found) {
				return;
			}
			const result = each.findChildWithId(id);
			if (result !== null) {
				found = result;
			}
		});

		return found;
	}

	findPreliminaryChildren() {
		const result = [];

		if (this.isPreliminary()) {
			result.push(this);
		}

		if (this.hasChildren()) {
			this.children().forEach((child) => {
				const preliminaryChildren = child.findPreliminaryChildren();
				result.push.apply(result, preliminaryChildren);
			});
		}

		return result;
	}

	setParent(newParent) {
		this._parent = newParent;
	}

	numberOfSubDirectories() {
		return -1;
	}

	numberOfDocuments() {
		return -1;
	}

	isPreliminary() {
		return false;
	}

	getLabel() {
		return this.isRoot() ? _("Archive") : this.name();
	}

	root() {
		if (this.isRoot()) {
			return this;
		}
		return this.parent().root();
	}

	buildWidget(_document, _position) {}

	buildWidgetFor(aView, position) {
		const widget = this._buildWidgetFor(aView, position);
		this._eventHandler.trigger("widgetBuilt", widget);
		return widget;
	}

	canBeMoved() {
		return this._filePermissions.canMove();
	}

	canBeRenamed() {
		return this._filePermissions.canRename();
	}

	canBeDeleted() {
		return this._filePermissions.canDelete();
	}

	canPermissionsBeEdited() {
		return this._filePermissions.canEditPermissions();
	}

	canBeAccessedByCompanyUser(user, company) {
		return this._filePermissions.allowsAccessToCompanyUser(user, company);
	}

	type() {
		return "";
	}

	isFolder() {
		return false;
	}

	isFile() {
		return false;
	}

	isRoot() {
		return false;
	}

	_identifier() {
		if (this._id) {
			return this._id.toString();
		}
		return "__root__";
	}

	_serializedProperties() {
		let data = super._serializedProperties();

		data.name = this._name;
		data.id = this._id;
		data.parentId = this.parent() && this.parent().id();

		data.created = this._creationTime;
		data.lastModified = this._lastModificationTime;

		if (this._filePermissions) {
			data = { ...data, ...this._filePermissions.serialize() };
		}

		return data;
	}

	static transformMaterializationData(data) {
		data.creationTime = data.created;
		data.lastModificationTime = data.lastModified;
		data.filePermissions = FilePermissions.materialize(data);
	}

	static sortByNameAZStrategy(anEntry, anotherEntry) {
		if (anEntry.type() === anotherEntry.type()) {
			if (anEntry.name() < anotherEntry.name()) {
				return -1;
			}
			if (anEntry.name() === anotherEntry.name()) {
				return 0;
			}
			if (anEntry.name() > anotherEntry.name()) {
				return 1;
			}
		}
		if (anEntry.isFolder()) {
			return -1;
		}
		return 1;
	}

	static sortByNameZAStrategy(anEntry, anotherEntry) {
		if (anEntry.type() === anotherEntry.type()) {
			if (anEntry.name() < anotherEntry.name()) {
				return 1;
			}
			if (anEntry.name() === anotherEntry.name()) {
				return 0;
			}
			if (anEntry.name() > anotherEntry.name()) {
				return -1;
			}
		}
		if (anEntry.isFolder()) {
			return 1;
		}
		return -1;
	}

	static sortByCreationTimeAscStrategy(anEntry, anotherEntry) {
		if (anEntry.creationTime() < anotherEntry.creationTime()) {
			return 1;
		}
		if (anEntry.creationTime() > anotherEntry.creationTime()) {
			return -1;
		}
		return 0;
	}

	static sortByCreationTimeDescStrategy(anEntry, anotherEntry) {
		if (anEntry.creationTime() > anotherEntry.creationTime()) {
			return 1;
		}
		if (anEntry.creationTime() < anotherEntry.creationTime()) {
			return -1;
		}
		return 0;
	}

	static sortByModificationTimeAscStrategy(anEntry, anotherEntry) {
		if (
			anEntry.lastModificationTime() < anotherEntry.lastModificationTime()
		) {
			return 1;
		}
		if (
			anEntry.lastModificationTime() > anotherEntry.lastModificationTime()
		) {
			return -1;
		}
		return 0;
	}

	static sortByModificationTimeDescStrategy(anEntry, anotherEntry) {
		if (
			anEntry.lastModificationTime() > anotherEntry.lastModificationTime()
		) {
			return 1;
		}
		if (
			anEntry.lastModificationTime() < anotherEntry.lastModificationTime()
		) {
			return -1;
		}
		return 0;
	}
}
